import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';

const initialState = {
	data: {},
	timeStamp: null,
	loading: true,
	error: null,
	searchable: true,

	// ✅ Complete States
	assetGroupsLoadingComplete: false,
	completeAddGroup: false,
	completeDeleteGroup: false,
	completeAddGroupAsset: false,
	completeDeleteGroupAsset: false,

	// ✅ Loading States
	loadingAddGroup: false,
	loadingDeleteGroup: false,
	loadingAddGroupAsset: false,
	loadingDeleteGroupAsset: false,

	updateGroupLoading: false,
	deleteGroupLoading: false,

	groupAssetsLoading: false,
	addGroupAssetLoading: false,
	removeGroupAssetLoading: false
};

const assetGroupSlice = createSlice({
	name: 'assetGroup',
	initialState,
	reducers: {
		loadGroups(state) {
			state.loading = true;
			state.error = null;
		},
		loadGroupsSuccess(state, action) {
			state.timeStamp = Date.now();
			state.loading = false;
			const { orgID, data } = action.payload;
			const oldData = state.data[orgID] || [];
			state.data[orgID] = mergeReduxStateData(oldData, data, 'groupID');
			state.assetGroupsLoadingComplete = true;
		},
		loadGroupsError(state, action) {
			state.loading = false;
			state.error = action.payload;
		},

		loadAssetsGroups(state) {
			state.loading = true;
			state.error = null;
		},
		loadAssetsGroupsSuccess(state, action) {
			const { orgID, data } = action.payload;

			// data is an object with groupID as key and devices as value array of devices
			// Merge the devices with the groups
			state.data[orgID] = state.data[orgID].map((group) => {
				const devices = data[group.groupID] || [];
				return { ...group, devices };
			});
		},
		loadAssetsGroupsError(state, action) {
			state.loading = false;
			state.error = action.payload;
		},

		// Add Group
		addGroup(state) {
			state.loadingAddGroup = true;
			state.completeAddGroup = false;
			state.error = null;
		},
		addGroupSuccess(state) {
			state.loadingAddGroup = false;
			state.completeAddGroup = true;
		},
		addGroupError(state, action) {
			state.error = action.payload;
			state.loadingAddGroup = false;
			state.completeAddGroup = false;
		},

		// Update Group
		updateGroup(state) {
			state.updateGroupLoading = true;
			state.error = null;
		},
		updateGroupSuccess(state) {
			state.updateGroupLoading = false;
		},
		updateGroupError(state, action) {
			state.error = action.payload;
			state.updateGroupLoading = false;
		},

		// Delete Group
		deleteGroup(state) {
			state.loadingDeleteGroup = true;
			state.completeDeleteGroup = false;
			state.error = null;
		},
		deleteGroupSuccess(state) {
			state.loadingDeleteGroup = false;
			state.completeDeleteGroup = true;
		},
		deleteGroupError(state, action) {
			state.error = action.payload;
			state.loadingDeleteGroup = false;
			state.completeDeleteGroup = false;
		},

		// Load Group Assets
		loadGroupAssets(state) {
			state.groupAssetsLoading = true;
			state.error = null;
		},
		loadGroupAssetsSuccess(state, action) {
			const { orgID, groupID, data } = action.payload;
			state.data[orgID] = state.data[orgID].map((group) =>
				group.groupID === groupID ? { ...group, devices: data } : group
			);
			state.groupAssetsLoading = false;
		},
		loadGroupAssetsError(state, action) {
			state.error = action.payload;
			state.groupAssetsLoading = false;
		},

		// Add Group assets
		addGroupAsset(state) {
			state.loadingAddGroupAsset = true;
			state.completeAddGroupAsset = false;
			state.error = null;
		},
		addGroupAssetSuccess(state) {
			state.loadingAddGroupAsset = false;
			state.completeAddGroupAsset = true;
		},
		addGroupAssetError(state, action) {
			state.error = action.payload;
			state.loadingAddGroupAsset = false;
			state.completeAddGroupAsset = false;
		},

		// Delete Group assets
		removeGroupAsset(state) {
			state.loadingDeleteGroupAsset = true;
			state.completeDeleteGroupAsset = false;
		},
		removeGroupAssetSuccess(state) {
			state.loadingDeleteGroupAsset = false;
			state.completeDeleteGroupAsset = true;
		},
		removeGroupAssetError(state, action) {
			state.error = action.payload;
			state.loadingDeleteGroupAsset = false;
			state.completeDeleteGroupAsset = false;
		}

		// Add more actions here
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => {
			return initialState; // Reset state on logout
		});
	}
});

// Exporting the actions and reducer
export const assetGroupActions = assetGroupSlice.actions;

export default assetGroupSlice.reducer;
